import { useState, useCallback, useRef, useEffect } from 'react';
import MintelTracking from 'mintel-user-tracking';
import MintelTrackingSST from 'mintel-user-tracking-sst';

interface MintelTrackingType {
  setToken(token: string): void;
}
/**
 * A function to be passed to apps to initialize mixpanel
 * @callback startTracking
 * @param {string} currentProduct The current product
 * @param {boolean} production Whether to use the production mixpanel project
 * @param {boolean} sstEnabled Whether to use the experimental version of mintel-user-tracking, to enable SST
 * @returns {Object} The mintel-user-tracking object
 */

/**
 * Returns a function to initialize mixpanel through mintel-user-tracking using
 * the current portal JWT and account manager. The returned function takes the
 * current product name and if production as arguments. It will block mixpanel
 * being initialized twice on the same product.
 * @param {string} accessToken Portal access token
 * @param {string} oidcAccessToken OIDC access token
 * @returns {startTracking | false} The function to start tracking or false
 */
export default (
  accessToken: string | undefined,
  oidcAccessToken: string,
  accountManager?: string,
): typeof MintelTracking | typeof MintelTrackingSST | false => {
  const [initializedProduct, setInitializedProduct] = useState('');
  const [initializedMintelUserTracking, setInitializedMintelUserTracking] =
    useState(false);
  const mintelTrackingRef = useRef<MintelTrackingType | null>(null);

  const startTracking = useCallback(
    (currentProduct: string, production = false, sstEnabled = false) => {
      const mintelTracking = sstEnabled ? MintelTrackingSST : MintelTracking;
      if (
        initializedProduct !== currentProduct ||
        initializedMintelUserTracking !== sstEnabled
      ) {
        mintelTracking.handleMixpanelInit(
          accessToken,
          currentProduct,
          accountManager,
          production,
          ...(sstEnabled ? [oidcAccessToken] : []),
        );
        setInitializedMintelUserTracking(sstEnabled);
        setInitializedProduct(currentProduct);
      }
      mintelTrackingRef.current = mintelTracking;
      return mintelTracking;
    },
    [
      accessToken,
      accountManager,
      initializedProduct,
      initializedMintelUserTracking,
      oidcAccessToken,
      setInitializedProduct,
      setInitializedMintelUserTracking,
    ],
  );

  useEffect(() => {
    if (
      mintelTrackingRef.current &&
      typeof mintelTrackingRef.current.setToken === 'function'
    ) {
      mintelTrackingRef.current.setToken(oidcAccessToken);
    }
  }, [oidcAccessToken]);

  // If there is an oidcAccessToken but no accessToken or accountManager, wait
  // for them to load, otherwise this is a public user which we want to track.
  if (oidcAccessToken && (!accessToken || !accountManager)) {
    return false;
  }

  return startTracking;
};
